/**
 * useHasKennel — szybkie sprawdzenie czy zalogowany user ma już hodowlę.
 * Zwraca:
 *   - loading: boolean (pierwszy fetch)
 *   - hasKennel: boolean | null (null = jeszcze nieznane)
 *   - firstKennelId: string | null
 *
 * Cache w sessionStorage żeby nie spamować API przy nawigacji.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

const CACHE_KEY = 'apisai:has-kennel:v1';

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch { /* SSR / private mode */ }
}

export function clearHasKennelCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch { /* noop */ }
}

export default function useHasKennel() {
  const { user } = useAuth();
  const cached = readCache();
  const [hasKennel, setHasKennel] = useState(cached?.hasKennel ?? null);
  const [firstKennelId, setFirstKennelId] = useState(cached?.firstKennelId ?? null);
  const [loading, setLoading] = useState(!cached && !!user);

  useEffect(() => {
    if (!user) {
      setHasKennel(null);
      setFirstKennelId(null);
      return;
    }
    let alive = true;
    setLoading(true);
    const token = localStorage.getItem('apisai_token') || '';
    axios
      .get(`${API}/kennels`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 6000,
      })
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.kennels || []);
        const has = list.length > 0;
        const first = has ? (list[0].id || list[0].slug || null) : null;
        setHasKennel(has);
        setFirstKennelId(first);
        writeCache({ hasKennel: has, firstKennelId: first });
      })
      .catch(() => {
        if (alive) {
          setHasKennel(false);
          setFirstKennelId(null);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [user]);

  return { loading, hasKennel, firstKennelId };
}
